/**
 * Facebook 广告组 targeting 版位相关常量（与后端 internal/account/service_facebook_create.go 注释对齐）
 */

export const DEFAULT_PUBLISHER_PLATFORMS = ['facebook', 'instagram', 'audience_network', 'messenger'] as const;

/** 供 UI 展示的发布平台（部分平台可能在当前系统/接口中暂不可用，需置灰） */
export const PUBLISHER_PLATFORM_OPTIONS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'audience_network', label: 'Audience Network' },
  { value: 'messenger', label: 'Messenger' },
  // Meta 页面中常见但本系统尚未在后端注释/字段中显式支持，先按「不可用」展示（置灰）
  { value: 'whatsapp', label: 'WhatsApp', disabled: true },
  { value: 'threads', label: 'Threads', disabled: true },
] as const;

export const DEFAULT_FACEBOOK_POSITIONS = [
  'feed',
  'story',
  'marketplace',
  'right_hand_column',
  'search',
] as const;

export const DEFAULT_INSTAGRAM_POSITIONS = ['stream', 'story', 'reels', 'explore'] as const;

export const DEFAULT_DEVICE_PLATFORMS = ['mobile', 'desktop'] as const;

/** 可选操作系统；默认不选，与「选 OS 时须含 mobile」校验一致 */
export const USER_OS_OPTIONS = ['iOS', 'Android'] as const;

export type FacebookPlacementMode = 'automatic' | 'manual';

export const PUBLISHER_PLATFORM_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  audience_network: 'Audience Network',
  messenger: 'Messenger',
  whatsapp: 'WhatsApp',
  threads: 'Threads',
};

export const FACEBOOK_POSITION_LABELS: Record<string, string> = {
  feed: '动态消息',
  story: '快拍',
  marketplace: 'Marketplace',
  right_hand_column: '右侧栏',
  search: '搜索',
};

export const INSTAGRAM_POSITION_LABELS: Record<string, string> = {
  stream: '动态',
  story: '快拍',
  reels: 'Reels',
  explore: '发现',
};

export const DEVICE_PLATFORM_LABELS: Record<string, string> = {
  mobile: '移动设备',
  desktop: '桌面设备',
};

export const USER_OS_LABELS: Record<string, string> = {
  iOS: 'iOS',
  Android: 'Android',
};

export type PlacementSelection =
  | { kind: 'platform'; value: string }
  | { kind: 'facebook_position'; value: string }
  | { kind: 'instagram_position'; value: string }
  | { kind: 'device_platform'; value: string }
  | { kind: 'user_os'; value: string }
  | { kind: 'unsupported'; value: string };

export type PlacementGroupItem = {
  id: string;
  label: string;
  description?: string;
  note?: string;
  disabled?: boolean;
  selection: PlacementSelection;
};

export type PlacementGroup = {
  id: string;
  label: string;
  description?: string;
  items: readonly PlacementGroupItem[];
};

export const META_STYLE_PLACEMENT_GROUPS: readonly PlacementGroup[] = [
  {
    id: 'feeds',
    label: '动态信息流',
    description: '借助动态信息流广告，为你的商企大幅提高可见度。',
    items: [
      { id: 'fb-feed', label: 'Facebook 动态消息', selection: { kind: 'facebook_position', value: 'feed' } },
      { id: 'ig-stream', label: 'Instagram 动态', selection: { kind: 'instagram_position', value: 'stream' } },
      { id: 'fb-marketplace', label: 'Facebook Marketplace', selection: { kind: 'facebook_position', value: 'marketplace' } },
      {
        id: 'fb-video-feeds',
        label: 'Facebook 视频动态',
        disabled: true,
        note: 'Meta 当前 API 版本已停用 Facebook 视频动态版位，本系统不再保存该版位。',
        selection: { kind: 'unsupported', value: 'facebook_video_feeds' },
      },
      { id: 'ig-explore', label: 'Instagram 探索首页', selection: { kind: 'instagram_position', value: 'explore' } },
      {
        id: 'threads-feed',
        label: 'Threads 动态',
        disabled: true,
        note: '当前系统后端未提供 Threads 版位字段，暂不可单独保存。',
        selection: { kind: 'unsupported', value: 'threads_feed' },
      },
      {
        id: 'fb-notification',
        label: 'Facebook 通知',
        disabled: true,
        note: '当前系统后端未提供 Facebook 通知版位字段。',
        selection: { kind: 'unsupported', value: 'facebook_notification' },
      },
    ],
  },
  {
    id: 'stories-reels',
    label: '快拍、Reels',
    description: '使用全屏竖向版位，以更接近 Meta 后台的方式管理快拍和 Reels。',
    items: [
      { id: 'ig-story', label: 'Instagram 快拍', selection: { kind: 'instagram_position', value: 'story' } },
      { id: 'fb-story', label: 'Facebook 快拍', selection: { kind: 'facebook_position', value: 'story' } },
      { id: 'ig-reels', label: 'Instagram Reels', selection: { kind: 'instagram_position', value: 'reels' } },
      {
        id: 'fb-reels',
        label: 'Facebook Reels',
        disabled: true,
        note: 'Meta 当前不接受把 reels 作为 facebook_positions 传入，本系统先不保存 Facebook Reels。',
        selection: { kind: 'unsupported', value: 'facebook_reels' },
      },
      {
        id: 'messenger-story',
        label: 'Messenger 快拍',
        disabled: true,
        note: '当前系统后端未提供 Messenger 快拍的单独版位字段。',
        selection: { kind: 'unsupported', value: 'messenger_story' },
      },
      {
        id: 'whatsapp-status',
        label: 'WhatsApp 动态',
        disabled: true,
        note: '当前系统后端未提供 WhatsApp 动态版位字段。',
        selection: { kind: 'unsupported', value: 'whatsapp_status' },
      },
    ],
  },
  {
    id: 'search-surface',
    label: '搜索与发现',
    description: '覆盖搜索类入口。',
    items: [
      { id: 'fb-search', label: 'Facebook 搜索结果', selection: { kind: 'facebook_position', value: 'search' } },
      {
        id: 'ig-search',
        label: 'Instagram 搜索结果',
        disabled: true,
        note: '当前系统后端未提供 Instagram 搜索结果字段，可先用探索首页承接。',
        selection: { kind: 'unsupported', value: 'instagram_search' },
      },
    ],
  },
  {
    id: 'apps-sites',
    label: '应用和网站',
    description: '这些位置由平台维度控制，目前不支持再细分到更小子版位。',
    items: [
      { id: 'audience-network', label: 'Audience Network', selection: { kind: 'platform', value: 'audience_network' } },
      { id: 'messenger-platform', label: 'Messenger', selection: { kind: 'platform', value: 'messenger' } },
    ],
  },
];

export function toPlacementSelectOptions(values: readonly string[], labels: Record<string, string>) {
  return values.map((v) => ({ value: v, label: labels[v] ?? v }));
}

export type FacebookPlacementState = {
  publisherPlatforms: string[];
  facebookPositions: string[];
  instagramPositions: string[];
  devicePlatforms: string[];
  userOS: string[];
};

/** 新建广告组：默认全选（操作系统默认不选） */
export function getDefaultFacebookPlacementState(): FacebookPlacementState {
  return {
    publisherPlatforms: [...DEFAULT_PUBLISHER_PLATFORMS],
    facebookPositions: [...DEFAULT_FACEBOOK_POSITIONS],
    instagramPositions: [...DEFAULT_INSTAGRAM_POSITIONS],
    devicePlatforms: [...DEFAULT_DEVICE_PLATFORMS],
    userOS: [],
  };
}

const uniq = (values: string[]) => Array.from(new Set(values.filter(Boolean)));

export function normalizeFacebookPlacementState(state: FacebookPlacementState): FacebookPlacementState {
  const publisherPlatforms = uniq(state.publisherPlatforms);
  const devicePlatforms = uniq(state.devicePlatforms.filter((value) => DEFAULT_DEVICE_PLATFORMS.includes(value as typeof DEFAULT_DEVICE_PLATFORMS[number])));
  const facebookPositions = uniq(state.facebookPositions.filter((value) => DEFAULT_FACEBOOK_POSITIONS.includes(value as typeof DEFAULT_FACEBOOK_POSITIONS[number])));
  const instagramPositions = uniq(state.instagramPositions.filter((value) => DEFAULT_INSTAGRAM_POSITIONS.includes(value as typeof DEFAULT_INSTAGRAM_POSITIONS[number])));
  const userOS = uniq(state.userOS.filter((value) => USER_OS_OPTIONS.includes(value as typeof USER_OS_OPTIONS[number])));

  const nextPublisherPlatforms = [...publisherPlatforms];
  if (facebookPositions.length > 0 && !nextPublisherPlatforms.includes('facebook')) nextPublisherPlatforms.push('facebook');
  if (instagramPositions.length > 0 && !nextPublisherPlatforms.includes('instagram')) nextPublisherPlatforms.push('instagram');

  const nextFacebookPositions = nextPublisherPlatforms.includes('facebook') ? facebookPositions : [];
  const nextInstagramPositions = nextPublisherPlatforms.includes('instagram') ? instagramPositions : [];
  const nextDevicePlatforms = [...devicePlatforms];
  if (userOS.length > 0 && !nextDevicePlatforms.includes('mobile')) nextDevicePlatforms.push('mobile');

  return {
    publisherPlatforms: nextPublisherPlatforms,
    facebookPositions: nextFacebookPositions,
    instagramPositions: nextInstagramPositions,
    devicePlatforms: nextDevicePlatforms,
    userOS,
  };
}
